import React from 'react';

import { useEncounterWorkDisposition } from '../../../hooks/useEncounterWorkDisposition';
import type { ClaimWorkQueueItem } from '../../../store';
import { QueueRowMenu } from '../work';

import { claimQueueRowMenuState } from './queueRowMenu';

export interface ClaimQueueRowMenuProps {
  item: ClaimWorkQueueItem;
  /** The row's service date, already formatted for reading. */
  serviceDate: string;
  encounterId: string;
  onOpenInWorkbench: () => void;
}

/**
 * The Claims worklist's row kebab: packet D's menu and hook, per row.
 *
 * A component per row rather than one menu hoisted into the tab, because the
 * hook is keyed by encounter and owns that row's in-flight state. The menu,
 * the dialogs, the snackbar and the Undo all belong to D; this is only the
 * wiring, and the one thing the Claims surface knows that D cannot: the
 * subject line ("Bea Lee · 12 Aug · Aetna") the archive dialog reads back.
 */
export const ClaimQueueRowMenu: React.FC<ClaimQueueRowMenuProps> = ({
  encounterId,
  item,
  onOpenInWorkbench,
  serviceDate,
}) => {
  /* The list renders a missing service date as a dash; the archive dialog
     and its snackbar are sentences, and a dash standing in for a date reads
     as punctuation gone wrong. Absent means absent here. */
  const readableDate = serviceDate === '—' ? '' : serviceDate;
  const { hidden, subject } = claimQueueRowMenuState(item, readableDate);
  const { addNote, archive, pending, restore, snooze } = useEncounterWorkDisposition({
    claimId: item.claim_id,
    encounterId,
    source: 'claims_worklist',
    subjectLabel: [subject.patientName, readableDate].filter(Boolean).join(' · '),
  });

  return (
    <QueueRowMenu
      busy={pending !== null}
      hidden={hidden}
      onAddNote={opts => void addNote(opts)}
      onArchive={opts => void archive(opts)}
      onOpenInWorkbench={onOpenInWorkbench}
      onRestore={() => void restore()}
      onSnooze={opts => void snooze(opts)}
      subject={subject}
    />
  );
};
